import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';

const heroContent = {
  nl: {
    eyebrow: 'Digitale partner voor zelfstandigen & KMO\'s',
    title: 'Websites die meegroeien met je bedrijf',
    subtitle: 'Van een professionele bedrijfswebsite tot klantportalen, planning en digitale bedrijfstools. Netblitz bouwt één oplossing rond de manier waarop jouw onderneming werkt.',
    ctaPrimary: 'Vraag een gratis offerte aan',
    ctaSecondary: 'Bekijk onze pakketten',
    ctaChatbot: 'Direct offerte via chatbot',
    note: 'Tip: Klik op de chatbot rechtsonder om direct een offerte op maat te ontvangen!'
  },
  en: {
    eyebrow: 'Digital partner for freelancers & SMEs',
    title: 'Websites that grow with your business',
    subtitle: 'From a professional business website to client portals, planning and digital business tools. Netblitz builds one solution around the way your company works.',
    ctaPrimary: 'Request a free quote',
    ctaSecondary: 'View our packages',
    ctaChatbot: 'Get quote via chatbot',
    note: 'Tip: Click the chatbot in the bottom right to receive a custom quote directly!'
  },
  fr: {
    eyebrow: 'Partenaire digital pour indépendants & PME',
    title: 'Des sites qui grandissent avec votre entreprise',
    subtitle: 'D\'un site professionnel aux portails clients, au planning et aux outils digitaux. Netblitz construit une solution autour de la façon dont votre entreprise fonctionne.',
    ctaPrimary: 'Demander un devis gratuit',
    ctaSecondary: 'Voir nos forfaits',
    ctaChatbot: 'Devis via chatbot',
    note: 'Astuce: Cliquez sur le chatbot en bas à droite pour recevoir un devis personnalisé!'
  },
  de: {
    eyebrow: 'Digitaler Partner für Selbstständige & KMU',
    title: 'Websites, die mit Ihrem Unternehmen wachsen',
    subtitle: 'Von einer professionellen Unternehmenswebsite bis zu Kundenportalen, Planung und digitalen Business-Tools. Netblitz baut eine Lösung rund um Ihre Arbeitsweise.',
    ctaPrimary: 'Kostenloses Angebot anfordern',
    ctaSecondary: 'Unsere Pakete ansehen',
    ctaChatbot: 'Angebot via Chatbot',
    note: 'Tipp: Klicken Sie auf den Chatbot unten rechts, um direkt ein Angebot zu erhalten!'
  }
};

export const HeroSection = () => {
  const { language } = useLanguage();
  const content = heroContent[language] || heroContent.nl;
  const contactPath = getLocalizedPath('contact', language);
  const pricingPath = getLocalizedPath('pricing', language);

  const openChatbot = () => {
    // Open the chatbot directly in quote-calculator mode
    window.dispatchEvent(new CustomEvent('netblitz:open-quote'));
  };

  return (
    <section 
      className="relative min-h-[90vh] flex items-center py-20 overflow-hidden bg-[#F9FAFB]"
      data-testid="hero-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#2563EB]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#F97316]/5 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Eyebrow */}
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-6 bg-blue-100 text-[#2563EB] border border-blue-200"
          >
            {content.eyebrow}
          </motion.span>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#111827] mb-6 leading-tight tracking-tight"
          >
            {content.title}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg sm:text-xl text-[#4B5563] mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            {content.subtitle}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6"
          >
            <Link
              to={contactPath}
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white bg-[#2563EB] hover:bg-[#1D4ED8] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
              data-testid="hero-cta-primary"
            >
              {content.ctaPrimary}
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to={pricingPath}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-[#111827] border-2 border-gray-300 hover:border-[#2563EB] hover:text-[#2563EB] hover:bg-gray-50 transition-all duration-300"
              data-testid="hero-cta-secondary"
            >
              {content.ctaSecondary}
            </Link>
          </motion.div>

          {/* Chatbot CTA - Prominent */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-8"
          >
            <button
              onClick={openChatbot}
              className="group inline-flex items-center gap-3 px-6 py-3 rounded-full font-semibold text-white bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#C2410C] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 animate-pulse hover:animate-none"
              data-testid="hero-chatbot-cta"
            >
              <MessageCircle size={22} className="animate-bounce" />
              {content.ctaChatbot}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          {/* Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#FEF3C7] border border-[#F59E0B]/30"
          >
            <MessageCircle size={20} className="text-[#F97316] flex-shrink-0" />
            <p className="text-sm text-[#92400E] text-left font-medium">
              {content.note}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
